import type { Tenant } from '@/types/database.types'

const TTL = 5 * 60 * 1000 // 5 minutes
const MAX_SIZE = 50

interface CacheEntry {
  tenant: Tenant
  expires: number
}

const tenantCache = new Map<string, CacheEntry>()

/**
 * Retrieve a tenant from the in-memory LRU cache by slug.
 *
 * @param slug - Tenant subdomain slug
 * @returns The cached `Tenant` object, or `null` if missing/expired
 */
export function getCachedTenant(slug: string): Tenant | null {
  const entry = tenantCache.get(slug)
  if (!entry) return null

  if (entry.expires > Date.now()) {
    return entry.tenant
  }

  tenantCache.delete(slug)
  return null
}

/**
 * Store a tenant in the in-memory cache with LRU eviction.
 * Evicts the oldest entry when the cache exceeds {@link MAX_SIZE} (50).
 *
 * @param slug - Tenant subdomain slug
 * @param tenant - Tenant record to cache
 */
export function setCachedTenant(slug: string, tenant: Tenant): void {
  // LRU eviction
  if (tenantCache.size >= MAX_SIZE) {
    const oldestKey = tenantCache.keys().next().value
    if (oldestKey) tenantCache.delete(oldestKey)
  }

  tenantCache.set(slug, {
    tenant,
    expires: Date.now() + TTL,
  })
}

/**
 * Invalidate tenant cache entries.
 *
 * @param slug - If provided, invalidates only that slug; otherwise clears the entire cache
 */
export function invalidateTenantCache(slug?: string): void {
  if (slug) {
    tenantCache.delete(slug)
  } else {
    tenantCache.clear()
  }
}
